import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Sparkles,
  Wand2,
  Clock,
  MessageSquare,
  Plus,
  X,
  Save,
  Upload,
  Loader2,
  Check,
  Copy,
  ExternalLink,
  Mail,
  Bell,
  Eye,
  Share2,
  Image,
  User,
  Lock,
  ArrowRight,
  Wifi,
  Youtube,
  Facebook,
  Linkedin,
} from "lucide-react";
import Calendar from "../../calendar";
import "../../calendar/Calendar.css";
import {
  createScheduledMeeting,
  updateScheduledMeeting,
  getScheduledMeetingById,
  getMeetingRooms,
} from "../../../services/roomServices";
import {
  generateWebinarTitle,
  generateWebinarDescription,
  suggestWebinarTimes,
  generateWebinarTalkingPoints,
} from "../../../services/openaiService";
import {
  listEventLandingPages,
  createEventLandingPage,
} from "../../../services/eventLandingServices";
import EventLandingPageModal from "../pages/event-landing/EventLandingPageModal";
import CalendarShareDropdown from "../../common/CalendarShareDropdown";
import { toast } from "react-toastify";
import { TIMEZONES } from "../../../utils/timezones";
import { useSiteContext } from "../../../context/SiteContext";

// ── Reusable Components ──────────────────────────────────────────────

const ToggleSwitch = ({ enabled, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`relative inline-flex w-[33px] h-[19px] items-center rounded-full transition-colors bg-[#282828]`}
  >
    <span
      className={`inline-block w-4 h-4 transform rounded-full transition-transform ${enabled
        ? "bg-[#832CFF] translate-x-[15px]"
        : "bg-[#7F7F7F] translate-x-0.5"
        }`}
    />
  </button>
);

const PurpleCheckbox = ({ checked, onChange, label, description }) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <div className="mt-0.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <div
        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${checked
          ? "bg-[#832CFF] border-[#832CFF]"
          : "bg-transparent border-gray-500"
          }`}
      >
        {checked && (
          <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
            <path
              d="M2 6L5 9L10 3"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
    </div>
    <div>
      <div className="text-white text-sm font-medium">{label}</div>
      {description && (
        <div className="text-gray-400 text-xs mt-0.5">{description}</div>
      )}
    </div>
  </label>
);

const CollapsibleSection = ({ title, expanded, onToggle, children }) => (
  <div className="w-full border border-white/10 rounded-xl overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-5 py-3.5 bg-[#832CFF]/20 hover:bg-[#832CFF]/30 transition-colors"
    >
      <span className="text-white font-semibold text-sm">{title}</span>
      {expanded ? (
        <ChevronUp className="w-5 h-5 text-white" />
      ) : (
        <ChevronDown className="w-5 h-5 text-white" />
      )}
    </button>
    {expanded && (
      <div className="px-5 py-4 bg-[#0d1117]/50 space-y-4">{children}</div>
    )}
  </div>
);

const SocialShareButtons = ({ url, title, formData: fd }) => {
  const webinarTitle = title || "Join my webinar";

  // Build formatted date/time string
  const getDateTimeStr = () => {
    if (!fd?.startDate) return "";
    const date = new Date(fd.startDate).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    const time = `${fd.startHour || "12"}:${fd.startMinute || "00"} ${fd.startPeriod || "PM"}`;
    let tz = "";
    try {
      const tzId = fd.timezone?.split("(")[0].trim();
      if (tzId) { const parts = new Date().toLocaleTimeString("en-US", { timeZone: tzId, timeZoneName: "short" }).split(" "); tz = parts[parts.length - 1]; }
    } catch { /* ignore */ }
    return `${date} at ${time}${tz ? ` ${tz}` : ""}`;
  };

  const duration = fd?.customDuration || fd?.duration || "";
  const dateTimeStr = getDateTimeStr();
  const description = fd?.webinarDescription ? fd.webinarDescription.replace(/<[^>]*>/g, "").trim() : "";

  // Email body — richest format
  const emailBody = [
    `You're invited to a live webinar!`,
    ``,
    `${webinarTitle}`,
    dateTimeStr ? `Date & Time: ${dateTimeStr}` : "",
    duration ? `Duration: ${duration} minutes` : "",
    description ? `\n${description.substring(0, 300)}${description.length > 300 ? "..." : ""}` : "",
    ``,
    `Register now: ${url}`,
    ``,
    `Don't miss out — secure your spot today!`,
  ].filter(Boolean).join("\n");

  // Social post — shorter, engaging
  const socialText = [
    `Join me for "${webinarTitle}"`,
    dateTimeStr ? `${dateTimeStr}` : "",
    duration ? `(${duration} min)` : "",
    `\n\nRegister for free:`,
  ].filter(Boolean).join(" | ");

  // WhatsApp — medium length with emoji
  const whatsappText = [
    `*${webinarTitle}*`,
    ``,
    dateTimeStr ? `📅 ${dateTimeStr}` : "",
    duration ? `⏱ Duration: ${duration} minutes` : "",
    description ? `\n${description.substring(0, 200)}${description.length > 200 ? "..." : ""}` : "",
    ``,
    `👉 Register here: ${url}`,
  ].filter(Boolean).join("\n");

  // Twitter — concise with hashtag
  const tweetText = [
    `Join "${webinarTitle}"`,
    dateTimeStr ? `📅 ${dateTimeStr}` : "",
    `\n\nRegister for free 👇`,
  ].filter(Boolean).join(" ");

  const encodedUrl = encodeURIComponent(url);

  const channels = [
    {
      name: "Email", color: "#EA4335", icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" /></svg>
      ), href: `mailto:?subject=${encodeURIComponent(`You're Invited: ${webinarTitle}`)}&body=${encodeURIComponent(emailBody)}`
    },
    {
      name: "Facebook", color: "#1877F2", icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
      ), href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodeURIComponent(socialText)}`
    },
    {
      name: "X", color: "#000000", icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
      ), href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodedUrl}`
    },
    {
      name: "LinkedIn", color: "#0A66C2", icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
      ), href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`
    },
    {
      name: "WhatsApp", color: "#25D366", icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
      ), href: `https://wa.me/?text=${encodeURIComponent(whatsappText)}`
    },
    {
      name: "Pinterest", color: "#E60023", icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641 0 12.017 0z" /></svg>
      ), href: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodeURIComponent(socialText)}`
    },
  ];

  return (
    <div className="flex flex-wrap gap-2">
      {channels.map((ch) => (
        <a key={ch.name} href={ch.href} target={ch.name === "Email" ? "_self" : "_blank"} rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white text-xs font-medium transition-colors"
          title={`Share on ${ch.name}`}>
          <span style={{ color: ch.color }}>{ch.icon}</span>
          {ch.name}
        </a>
      ))}
    </div>
  );
};

const UploadArea = ({ onFileSelect, onRemove, file, existingUrl, recommendedText }) => {
  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) handleFile(files[0]);
  };

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;
    if (selectedFile.size > 3 * 1024 * 1024) {
      toast.error("File size must be less than 3 MB");
      return;
    }
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(selectedFile.type)) {
      toast.error("Please select a valid image file (JPG, PNG, etc.)");
      return;
    }
    onFileSelect(selectedFile);
  };

  return (
    <div className="mt-2">
      <div className="text-white text-xs font-medium mb-2">Upload Image</div>
      <div
        className="border border-dashed border-gray-600 rounded-lg p-6 bg-[#0d1117] hover:border-gray-400 transition-colors cursor-pointer"
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        <label className="flex flex-col items-center justify-center cursor-pointer">
          <Upload className="w-6 h-6 text-gray-400 mb-2" />
          <p className="text-gray-400 text-sm">Upload image</p>
          <p className="text-gray-500 text-xs mt-1">
            {recommendedText || "1366x1100 recommended"}
          </p>
          {file && <p className="text-purple-400 text-xs mt-2">{file.name}</p>}
          <input
            type="file"
            accept=".png,.jpg,.jpeg,.gif,.webp"
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.[0]) handleFile(e.target.files[0]);
            }}
          />
        </label>
      </div>
      {(file || existingUrl) && (
        <div className="relative flex justify-center mt-3 group">
          <img
            src={file ? URL.createObjectURL(file) : existingUrl}
            alt="Preview"
            className="max-w-full max-h-[150px] rounded-md object-contain"
          />
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="absolute top-1 right-1 p-1 rounded-full bg-red-500/80 hover:bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
              title="Remove image"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const TIME_OPTIONS = (() => {
  const minutes = ["00", "15", "30", "45"];
  const options = [];
  for (let hour = 1; hour <= 12; hour += 1) {
    minutes.forEach((minute) => {
      options.push({ value: `${hour}-${minute}`, label: `${hour}:${minute}` });
    });
  }
  return options;
})();


const DEFAULT_REGISTRATION_FIELDS = [
  { name: "First Name", enabled: true, required: true },
  { name: "Last Name", enabled: true, required: true },
  { name: "Email Address", enabled: true, required: true },
];

const STEP_LABELS = ["Webinar Details", "Event Landing Page", "Email Settings", "Review & Publish"];

// ── Step Indicator ───────────────────────────────────────────────────

const StepIndicator = ({ currentStep, steps, onStepClick }) => (
  <div className="flex items-center justify-between mb-8">
    {steps.map((label, idx) => {
      const stepNum = idx + 1;
      const isActive = stepNum === currentStep;
      const isCompleted = stepNum < currentStep;
      const isClickable = !!onStepClick;
      return (
        <React.Fragment key={label}>
          <div
            className={`flex flex-col items-center gap-1.5 min-w-0 ${isClickable ? "cursor-pointer" : ""}`}
            onClick={() => isClickable && onStepClick(stepNum)}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${isCompleted
                ? "bg-[#832CFF] border-[#832CFF] text-white"
                : isActive
                  ? "bg-transparent border-[#832CFF] text-[#832CFF]"
                  : "bg-transparent border-gray-600 text-gray-500"
                } ${isClickable ? "hover:border-[#832CFF] hover:text-[#832CFF]" : ""}`}
            >
              {isCompleted ? <Check className="w-4 h-4" /> : stepNum}
            </div>
            <span
              className={`text-xs text-center ${isActive ? "text-white font-semibold" : isCompleted ? "text-purple-300" : "text-gray-500"
                } ${isClickable ? "hover:text-white" : ""}`}
            >
              {label}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <div
              className={`flex-1 h-0.5 mx-2 mt-[-16px] ${stepNum < currentStep ? "bg-[#832CFF]" : "bg-gray-700"
                }`}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ── Main Form Component ──────────────────────────────────────────────

const WebinarScheduleForm = ({ initialData = null, roomId = null }) => {
  const navigate = useNavigate();
  const { user } = useSiteContext();
  const timezones = TIMEZONES;
  const isEditMode = !!roomId || !!initialData;
  const isEventLandingLayoutEnabled =
    process.env.REACT_APP_ENABLE_EVENT_LANDING_LAYOUT === "true" ||
    user?.email?.toLowerCase() === "krishan.kothari@gmail.com";

  // Helper functions
  const getCurrentDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const getCurrentTime = () => {
    const now = new Date();
    let hours = now.getHours();
    let minutes = now.getMinutes();
    const roundedMinutes = Math.round(minutes / 15) * 15;
    if (roundedMinutes === 60) {
      minutes = 0;
      hours = (hours + 1) % 24;
    } else {
      minutes = roundedMinutes;
    }
    const period = hours >= 12 ? "PM" : "AM";
    if (hours === 0) hours = 12;
    else if (hours > 12) hours = hours - 12;
    return { hour: hours, minute: minutes.toString().padStart(2, "0"), period };
  };

  const parseDate = (dateString, tz) => {
    if (!dateString) return getCurrentDate();
    const utcDate = new Date(dateString);
    // Extract year/month/day in the webinar's timezone so we get the correct
    // calendar date regardless of the browser's local timezone.
    try {
      const tzId = tz || "America/New_York";
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tzId,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(utcDate);
      const y = parseInt(parts.find((p) => p.type === "year")?.value);
      const m = parseInt(parts.find((p) => p.type === "month")?.value) - 1;
      const d = parseInt(parts.find((p) => p.type === "day")?.value);
      return new Date(y, m, d);
    } catch {
      const date = new Date(dateString);
      date.setHours(0, 0, 0, 0);
      return date;
    }
  };

  const parseTimeFromISO = (isoString, tz) => {
    if (!isoString) return getCurrentTime();
    const date = new Date(isoString);
    // Use the webinar's timezone to extract the correct local hour/minute
    try {
      const tzId = tz || "America/New_York";
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tzId,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).formatToParts(date);
      const hourPart = parts.find((p) => p.type === "hour");
      const minutePart = parts.find((p) => p.type === "minute");
      const periodPart = parts.find((p) => p.type === "dayPeriod");
      return {
        hour: parseInt(hourPart?.value || "12", 10),
        minute: (minutePart?.value || "00").padStart(2, "0"),
        period: (periodPart?.value || "AM").toUpperCase(),
      };
    } catch {
      // Fallback to browser local time
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const period = hours >= 12 ? "PM" : "AM";
      if (hours === 0) hours = 12;
      else if (hours > 12) hours = hours - 12;
      return { hour: hours, minute: minutes.toString().padStart(2, "0"), period };
    }
  };

  const parsePassword = (passwordString) => {
    if (!passwordString) return ["", "", "", "", "", ""];
    const passwordArray = passwordString.split("").slice(0, 6);
    while (passwordArray.length < 6) passwordArray.push("");
    return passwordArray;
  };

  const currentTime = getCurrentTime();

  // Default the timezone field to the user's Account Profile timezone when
  // creating a new webinar. Read from localStorage so it's available on first
  // render (SiteContext populates `user` from the same key in a useEffect).
  const getDefaultTimezone = () => {
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "null");
      if (stored?.timezone) return stored.timezone;
    } catch { /* fall through */ }
    return "America/New_York";
  };

  // ── State ──────────────────────────────────────────────────────────

  const [formData, setFormData] = useState({
    webinarTitle: "",
    internalName: "",
    description: "",
    roomId: "",
    recurringEvent: false,
    frequency: "WEEKLY",
    startDate: getCurrentDate(),
    endDate: getCurrentDate(),
    startHour: currentTime.hour,
    startMinute: currentTime.minute,
    startPeriod: currentTime.period,
    duration: "60",
    customDuration: "",
    timezone: getDefaultTimezone(),

    // Registration (Step 2)
    requireRegistration: true,
    registrationApprovalMode: "auto",
    registrationFields: [...DEFAULT_REGISTRATION_FIELDS],
    customRegistrationFields: [],
    registrationHeadline: "",
    registrationDescription: "",
    requireRegistrationForReplay: true,
    requirePasscode: false,
    passcode: "",
    confirmationEmailEnabled: true,
    confirmationEmailAdditionalCopy: "",
    includeCalendarFile: true,
    registrationTitle: "",
    registrationSubtitle: "",

    // Email Settings (Step 3)
    emailFromName: user?.name || "",
    emailReplyTo: user?.email || "",
    reminderEmail24h: true,
    reminderEmail1h: true,
    reminderEmailAdditionalCopy: "",
    replayEmailEnabled: true,

    // Recording
    autoRecordWebinar: true,

    // Stage Control
    allowStageAccess: true,
    allowCoHostsManage: true,
    autoSpotlight: true,
    preAssignPresenters: false,
    presenterEmails: [],
    presenterEmailInput: "",
    coHostEmails: [],
    coHostEmailInput: "",

    // Attendee Options
    showAttendeeList: true,
    showAttendeeCount: true,
    allowChat: true,
    allowReactions: true,
    waitingRoom: false,

    // Customizations
    customBackground: false,
    countdownTimer: false,
    countdownMinutes: "15",
    countdownCustomMinutes: "",
    waitingRoomTitle: "",
    waitingRoomMessage: "",

    // Legacy fields preserved for backend compat
    autoShareReplay: true,
    replayExpirationDate: "",
    replayAudience: "attendees",
    requirePassword: false,
    password: ["", "", "", "", "", ""],
    showPasswordInEmail: false,
    streamYouTubeUrl: "",
    streamYouTubeKey: "",
    streamYouTubeBroadcastId: "",
    streamFacebookUrl: "",
    streamFacebookKey: "",
    streamLinkedInUrl: "",
    streamLinkedInKey: "",
    youtubeEnabled: false,
    facebookEnabled: false,
    linkedInEnabled: false,
  });

  const [files, setFiles] = useState({
    customBackgroundImage: null,
    registrationCoverImage: null,
    registrationHeroImage: null,
    waitingRoomCoverImage: null,
  });

  const [existingImageUrls, setExistingImageUrls] = useState({
    customBackgroundImage: null,
    registrationCoverImage: null,
    registrationHeroImage: null,
    waitingRoomCoverImage: null,
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [showShareModal, setShowShareModal] = useState(false);
  const [createdWebinarUrl, setCreatedWebinarUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [rooms, setRooms] = useState([]);

  // Event Landing Page linking
  const [landingPageMode, setLandingPageMode] = useState("builtin"); // "builtin" | "linked"
  const [eventLandingPages, setEventLandingPages] = useState([]);
  const [selectedLandingPageId, setSelectedLandingPageId] = useState("");
  const [selectedLandingPage, setSelectedLandingPage] = useState(null);
  const [showCreateLandingPageModal, setShowCreateLandingPageModal] = useState(false);
  const [loadingLandingPages, setLoadingLandingPages] = useState(false);

  const [expandedSections, setExpandedSections] = useState({
    aiAssistant: false,
    registrationSettings: false,
    reminderSettings: false,
    stageControl: false,
    attendeeOptions: false,
    customizations: false,
    liveStreaming: false,
  });

  const [aiLoading, setAiLoading] = useState({
    title: false,
    description: false,
    times: false,
    talkingPoints: false,
  });
  const [aiResults, setAiResults] = useState({
    titles: null,
    times: null,
    talkingPoints: null,
  });
  const [aiSuggestionText, setAiSuggestionText] = useState("");
  const [aiSuggestionType, setAiSuggestionType] = useState(null); // "title" | "description" | "times" | "talkingPoints"
  const [aiTopicInput, setAiTopicInput] = useState("");

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // ── Data loading ──────────────────────────────────────────────────

  useEffect(() => {
    getMeetingRooms(1, 100)
      .then((res) => {
        const roomList = res?.data?.data || res?.data?.rooms || res?.data || [];
        setRooms(Array.isArray(roomList) ? roomList : []);
      })
      .catch(() => { });
  }, []);

  // Fetch user's event landing pages for linking
  const fetchEventLandingPages = () => {
    setLoadingLandingPages(true);
    listEventLandingPages(1, 100)
      .then((res) => {
        const pages = res?.data || [];
        setEventLandingPages(Array.isArray(pages) ? pages : []);
      })
      .catch(() => { })
      .finally(() => setLoadingLandingPages(false));
  };

  useEffect(() => {
    fetchEventLandingPages();
  }, []);

  const handleCreateLandingPage = async (payload) => {
    try {
      const res = await createEventLandingPage(payload);
      const created = res?.data;
      if (created) {
        toast.success("Event landing page created!");
        setShowCreateLandingPageModal(false);
        // Refresh list and select the newly created page
        fetchEventLandingPages();
        setSelectedLandingPageId(created.id || created._id);
        setSelectedLandingPage(created);
        setLandingPageMode("linked");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create event landing page");
    }
  };

  const handleSelectLandingPage = (pageId) => {
    setSelectedLandingPageId(pageId);
    const page = eventLandingPages.find((p) => (p.id || p._id) === pageId);
    setSelectedLandingPage(page || null);
  };

  const mapApiDataToFormData = (apiData) => {
    if (!apiData) return null;
    const startTime = parseTimeFromISO(apiData.scheduledStartTime, apiData.timezone);
    const startDate = parseDate(apiData.scheduledStartTime, apiData.timezone);

    return {
      webinarTitle: apiData.roomName ? apiData.roomName.slice(0, 100) : "",
      internalName: apiData.internalName || "",
      description: apiData.description || "",
      roomId: apiData.roomId || "",
      recurringEvent: apiData.recurringEvent ?? false,
      frequency: apiData.frequency || "WEEKLY",
      startDate,
      endDate: apiData.endDate ? parseDate(apiData.endDate, apiData.timezone) : startDate,
      startHour: startTime.hour,
      startMinute: startTime.minute,
      startPeriod: startTime.period,
      duration: apiData.WebinarDuration?.toString() || apiData.duration?.toString() || "60",
      customDuration: "",
      timezone: apiData.timezone
        ? apiData.timezone.replace(/\s*\(GMT[^)]*\)\s*$/, "").trim()
        : "America/New_York",
      requireRegistration: apiData.requireRegistration ?? false,
      registrationApprovalMode: apiData.registrationApprovalMode || "auto",
      registrationFields:
        apiData.registrationFields?.length > 0
          ? apiData.registrationFields
          : [...DEFAULT_REGISTRATION_FIELDS],
      customRegistrationFields: [],
      registrationHeadline: apiData.registrationHeadline || "",
      registrationDescription: apiData.registrationDescription || "",
      requireRegistrationForReplay: apiData.requireRegistrationForReplay ?? true,
      requirePasscode: apiData.requirePasscode ?? false,
      passcode: apiData.passcode || "",
      confirmationEmailEnabled: apiData.confirmationEmailEnabled ?? true,
      confirmationEmailAdditionalCopy: apiData.confirmationEmailAdditionalCopy || "",
      includeCalendarFile: apiData.includeCalendarFile ?? true,
      registrationTitle: apiData.registrationTitle || "",
      registrationSubtitle: apiData.registrationSubtitle || "",
      emailFromName: apiData.emailFromName || user?.name || "",
      emailReplyTo: apiData.emailReplyTo || user?.email || "",
      reminderEmail24h: apiData.reminderEmail24h ?? true,
      reminderEmail1h: apiData.reminderEmail1h ?? true,
      reminderEmailAdditionalCopy: apiData.reminderEmailAdditionalCopy || "",
      replayEmailEnabled: apiData.replayEmailEnabled ?? true,
      autoRecordWebinar: apiData.autoRecordWebinar ?? false,
      allowStageAccess: apiData.allowStageAccess ?? true,
      allowCoHostsManage: apiData.allowCoHostsManage ?? true,
      autoSpotlight: apiData.autoSpotlight ?? true,
      preAssignPresenters: apiData.preAssignPresenters ?? (apiData.emails?.length > 0) ?? false,
      presenterEmails: apiData.emails || [],
      presenterEmailInput: "",
      coHostEmails: apiData.coHostEmails || [],
      coHostEmailInput: "",
      showAttendeeList: apiData.showAttendeeList ?? true,
      showAttendeeCount: apiData.showAttendeeCount ?? true,
      allowChat: apiData.allowChat ?? true,
      allowReactions: apiData.allowReactions ?? true,
      waitingRoom: apiData.waitingRoom ?? false,
      customBackground: apiData.customBackgroundEnabled ?? false,
      countdownTimer: apiData.countdownTimer ?? false,
      countdownMinutes: apiData.countdownMinutes?.toString() || "15",
      countdownCustomMinutes: "",
      waitingRoomTitle: apiData.waitingRoomTitle || "",
      waitingRoomMessage: apiData.waitingRoomMessage || "",
      autoShareReplay: apiData.autoShareReplay ?? true,
      replayExpirationDate: apiData.replayExpirationDate ? apiData.replayExpirationDate.split('T')[0] : "",
      replayAudience: apiData.replayAudience || "attendees",
      requirePassword: apiData.requirePassword ?? false,
      password: parsePassword(apiData.password),
      showPasswordInEmail: apiData.showPasswordInEmail ?? false,
      streamYouTubeUrl: apiData.streamYouTubeUrl || "",
      streamYouTubeKey: apiData.streamYouTubeKey || "",
      streamYouTubeBroadcastId: apiData.streamYouTubeBroadcastId || "",
      streamFacebookUrl: apiData.streamFacebookUrl || "",
      streamFacebookKey: apiData.streamFacebookKey || "",
      streamLinkedInUrl: apiData.streamLinkedInUrl || "",
      streamLinkedInKey: apiData.streamLinkedInKey || "",
      youtubeEnabled: !!(apiData.streamYouTubeKey || apiData.streamYouTubeUrl),
      facebookEnabled: !!(apiData.streamFacebookKey || apiData.streamFacebookUrl),
      linkedInEnabled: !!(apiData.streamLinkedInKey || apiData.streamLinkedInUrl),
    };
  };

  useEffect(() => {
    if (roomId && !initialData) {
      setIsLoading(true);
      getScheduledMeetingById(roomId)
        .then((response) => {
          if (response?.data) {
            const mappedData = mapApiDataToFormData(response.data);
            if (mappedData) setFormData(mappedData);
            setExistingImageUrls({
              customBackgroundImage: response.data.meetingBackgroundImage || null,
              registrationCoverImage: response.data.registrationCoverImage || null,
              registrationHeroImage: response.data.registrationHeroImage || null,
              waitingRoomCoverImage: response.data.waitingRoomCoverImage || null,
            });
            // Restore linked event landing page if one was set
            if (response.data.eventLandingPageId) {
              setLandingPageMode("linked");
              setSelectedLandingPageId(response.data.eventLandingPageId);
              const page = eventLandingPages.find((p) => (p.id || p._id) === response.data.eventLandingPageId);
              if (page) setSelectedLandingPage(page);
            }
            // Set registration URL for share section in edit mode
            const webId = response.data.roomId || roomId;
            if (webId) {
              setCreatedWebinarUrl(`${window.location.origin}/webinar/${webId}/event`);
            }
          }
        })
        .catch(() => toast.error("Failed to load meeting data"))
        .finally(() => setIsLoading(false));
    }
  }, [roomId]);

  useEffect(() => {
    if (initialData) {
      const mappedData = mapApiDataToFormData(initialData);
      if (mappedData) setFormData(mappedData);
      if (initialData.eventLandingPageId) {
        setLandingPageMode("linked");
        setSelectedLandingPageId(initialData.eventLandingPageId);
        const page = eventLandingPages.find((p) => (p.id || p._id) === initialData.eventLandingPageId);
        if (page) setSelectedLandingPage(page);
      }
    }
  }, [initialData]);

  // Resolve selected landing page once list is loaded (for edit mode)
  useEffect(() => {
    if (selectedLandingPageId && eventLandingPages.length > 0 && !selectedLandingPage) {
      const page = eventLandingPages.find((p) => (p.id || p._id) === selectedLandingPageId);
      if (page) setSelectedLandingPage(page);
    }
  }, [eventLandingPages, selectedLandingPageId]);

  // ── Handlers ──────────────────────────────────────────────────────

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handlePresenterEmailAdd = () => {
    const email = formData.presenterEmailInput.trim();
    if (!email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (formData.presenterEmails.includes(email)) {
      toast.error("Email already added");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      presenterEmails: [...prev.presenterEmails, email],
      presenterEmailInput: "",
    }));
  };

  const handlePresenterEmailRemove = (emailToRemove) => {
    setFormData((prev) => ({
      ...prev,
      presenterEmails: prev.presenterEmails.filter((e) => e !== emailToRemove),
    }));
  };

  const handleCoHostEmailAdd = () => {
    const email = formData.coHostEmailInput.trim();
    if (!email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (formData.coHostEmails.includes(email)) {
      toast.error("Email already added");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      coHostEmails: [...prev.coHostEmails, email],
      coHostEmailInput: "",
    }));
  };

  const handleCoHostEmailRemove = (emailToRemove) => {
    setFormData((prev) => ({
      ...prev,
      coHostEmails: prev.coHostEmails.filter((e) => e !== emailToRemove),
    }));
  };

  const handleFileSelect = (fileType, file) => {
    setFiles((prev) => ({ ...prev, [fileType]: file }));
    if (file) {
      setExistingImageUrls((prev) => ({ ...prev, [fileType]: null }));
    }
  };

  const handleFileRemove = (fileType) => {
    setFiles((prev) => ({ ...prev, [fileType]: null }));
    setExistingImageUrls((prev) => ({ ...prev, [fileType]: null }));
  };

  const formatDisplayDate = (dateValue) => {
    if (!dateValue) return "";
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  // ── Registration field management ─────────────────────────────────

  const [customFieldInput, setCustomFieldInput] = useState("");

  const addRegistrationField = (fieldName) => {
    setFormData((prev) => {
      const exists = prev.registrationFields.some((f) => f.name === fieldName);
      if (exists) return prev;
      return {
        ...prev,
        registrationFields: [
          ...prev.registrationFields,
          { name: fieldName, enabled: true, required: false },
        ],
      };
    });
  };

  const removeRegistrationField = (fieldName) => {
    if (fieldName === "First Name" || fieldName === "Last Name" || fieldName === "Email Address") return;
    setFormData((prev) => ({
      ...prev,
      registrationFields: prev.registrationFields.filter((f) => f.name !== fieldName),
    }));
  };

  const toggleRegistrationFieldRequired = (fieldName) => {
    setFormData((prev) => ({
      ...prev,
      registrationFields: prev.registrationFields.map((f) =>
        f.name === fieldName ? { ...f, required: !f.required } : f
      ),
    }));
  };

  const addCustomField = () => {
    const name = customFieldInput.trim();
    if (!name) return;
    addRegistrationField(name);
    setCustomFieldInput("");
  };

  // ── AI Handlers ───────────────────────────────────────────────────

  const handleGenerateTitle = async () => {
    const topic = aiTopicInput?.trim() || formData.webinarTitle?.trim();
    const description = formData.description?.trim();
    if (!topic && !description) {
      toast.error("Please enter a topic first");
      return;
    }
    setAiLoading((p) => ({ ...p, title: true }));
    try {
      const titles = await generateWebinarTitle({
        description: description,
        topic: topic || description,
      });
      if (titles?.length > 0) {
        setAiResults((p) => ({ ...p, titles }));
        setAiSuggestionText(`"${titles[0]}"`);
        setAiSuggestionType("title");
      }
    } catch {
      toast.error("Failed to generate titles. Check OpenAI configuration.");
    } finally {
      setAiLoading((p) => ({ ...p, title: false }));
    }
  };

  const handleGenerateDescription = async () => {
    if (!formData.webinarTitle.trim() && !aiTopicInput.trim()) {
      toast.error("Enter a topic or title first to generate a description");
      return;
    }
    setAiLoading((p) => ({ ...p, description: true }));
    try {
      const desc = await generateWebinarDescription(formData.webinarTitle.trim() || aiTopicInput.trim());
      if (desc) {
        setAiSuggestionText(desc);
        setAiSuggestionType("description");
      }
    } catch {
      toast.error("Failed to generate description.");
    } finally {
      setAiLoading((p) => ({ ...p, description: false }));
    }
  };

  const handleSuggestTimes = async () => {
    setAiLoading((p) => ({ ...p, times: true }));
    try {
      const suggestions = await suggestWebinarTimes({
        title: formData.webinarTitle,
        description: formData.description,
        timezone: formData.timezone,
      });
      if (suggestions?.length > 0) {
        setAiResults((p) => ({ ...p, times: suggestions }));
        const text = suggestions
          .map((s) => `${s.day} ${s.time} — ${s.reason}`)
          .join("\n");
        setAiSuggestionText(text);
        setAiSuggestionType("times");
      }
    } catch {
      toast.error("Failed to suggest times.");
    } finally {
      setAiLoading((p) => ({ ...p, times: false }));
    }
  };

  const handleTalkingPoints = async () => {
    if (!formData.webinarTitle.trim() && !aiTopicInput.trim()) {
      toast.error("Enter a topic or title first to generate talking points");
      return;
    }
    setAiLoading((p) => ({ ...p, talkingPoints: true }));
    try {
      const points = await generateWebinarTalkingPoints({
        title: formData.webinarTitle.trim() || aiTopicInput.trim(),
        description: formData.description,
      });
      if (points?.length > 0) {
        setAiResults((p) => ({ ...p, talkingPoints: points }));
        const text = points
          .map(
            (tp, i) =>
              `${i + 1}. ${tp.topic}\n${(tp.subpoints || []).map((sp) => `   • ${sp}`).join("\n")}`
          )
          .join("\n\n");
        setAiSuggestionText(text);
        setAiSuggestionType("talkingPoints");
      }
    } catch {
      toast.error("Failed to generate talking points.");
    } finally {
      setAiLoading((p) => ({ ...p, talkingPoints: false }));
    }
  };

  const handleApplySuggestion = () => {
    if (!aiSuggestionText) return;
    if (aiSuggestionType === "title") {
      // Strip surrounding quotes if present
      const cleaned = aiSuggestionText.replace(/^"(.*)"$/, "$1");
      handleInputChange("webinarTitle", cleaned);
      toast.success("Title applied!");
    } else if (aiSuggestionType === "description") {
      handleInputChange("description", aiSuggestionText);
      toast.success("Description applied!");
    } else {
      toast.info("Suggestion shown for reference");
    }
  };

  const handleClearSuggestion = () => {
    setAiSuggestionText("");
    setAiSuggestionType(null);
  };

  // ── Validation & Submit ───────────────────────────────────────────

  const validateForm = (isDraft = false) => {
    const newErrors = {};
    if (!formData.webinarTitle.trim()) {
      newErrors.webinarTitle = "Please enter a webinar title";
    }
    if (!isDraft) {
      if (!formData.startDate) {
        newErrors.startDate = "Please select a start date";
      }
      if (!formData.roomId) {
        // Room auto-assigned by backend
      }
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstError = Object.values(newErrors)[0];
      toast.error(firstError);
      return false;
    }
    return true;
  };

  const formatDateTime = () => {
    if (!formData.startDate) return null;
    // Build a naive datetime string from the date + selected hour/minute/period.
    // This string represents the time in the webinar's timezone (the backend
    // interprets it via moment.tz(startTime, timezone)), so we must NOT use
    // browser-local Date methods which would shift the time if the user's
    // browser timezone differs from the webinar timezone.
    const date = new Date(formData.startDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    let hours = parseInt(formData.startHour, 10);
    const minutes = parseInt(formData.startMinute, 10);
    if (formData.startPeriod === "PM" && hours !== 12) hours += 12;
    else if (formData.startPeriod === "AM" && hours === 12) hours = 0;
    const hour = String(hours).padStart(2, "0");
    const minute = String(minutes).padStart(2, "0");
    return `${year}-${month}-${day}T${hour}:${minute}:00`;
  };

  const getCalendarStartUtc = () => {
    const naive = formatDateTime();
    if (!naive) return null;
    const tzId = (formData.timezone || "").split("(")[0].trim() || "UTC";
    try {
      const asIfUtc = new Date(naive + "Z");
      if (Number.isNaN(asIfUtc.getTime())) return null;
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tzId,
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
      }).formatToParts(asIfUtc);
      const get = (t) => parts.find((p) => p.type === t)?.value;
      const hour = get("hour") === "24" ? "00" : get("hour");
      const tzAsUtcMs = Date.UTC(
        Number(get("year")), Number(get("month")) - 1, Number(get("day")),
        Number(hour), Number(get("minute")), Number(get("second"))
      );
      const offset = asIfUtc.getTime() - tzAsUtcMs;
      return new Date(asIfUtc.getTime() + offset).toISOString();
    } catch {
      return new Date(naive).toISOString();
    }
  };

  const handleSubmit = async (e, status = "scheduled") => {
    if (e) e.preventDefault();
    const isDraft = status === "draft";
    if (!validateForm(isDraft)) return;

    setIsSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("roomName", formData.webinarTitle.trim());
      formDataToSend.append("roomType", "Webinar");
      formDataToSend.append("status", status);

      // New wizard fields
      if (formData.internalName) formDataToSend.append("internalName", formData.internalName.trim());
      if (formData.registrationHeadline) formDataToSend.append("registrationHeadline", formData.registrationHeadline.trim());
      if (formData.registrationDescription) formDataToSend.append("registrationDescription", formData.registrationDescription.trim());
      formDataToSend.append("requireRegistrationForReplay", formData.requireRegistrationForReplay);
      formDataToSend.append("requirePasscode", formData.requirePasscode);
      if (formData.requirePasscode && formData.passcode) formDataToSend.append("passcode", formData.passcode);
      formDataToSend.append("emailFromName", formData.emailFromName.trim());
      formDataToSend.append("emailReplyTo", formData.emailReplyTo.trim());
      formDataToSend.append("reminderEmail24h", formData.reminderEmail24h);
      formDataToSend.append("reminderEmail1h", formData.reminderEmail1h);
      formDataToSend.append("reminderEmailAdditionalCopy", formData.reminderEmailAdditionalCopy);
      formDataToSend.append("replayEmailEnabled", formData.replayEmailEnabled);
      if (files.registrationHeroImage) {
        formDataToSend.append("registrationHeroImage", files.registrationHeroImage);
      } else if (!existingImageUrls.registrationHeroImage) {
        formDataToSend.append("removeRegistrationHeroImage", "true");
      }

      // Timezone
      let timezoneValue = "America/New_York";
      if (formData.timezone) {
        if (formData.timezone.includes("(")) {
          timezoneValue = formData.timezone.split("(")[0].trim();
        } else {
          const tz = timezones.find(
            (t) => t.value === formData.timezone || t.label === formData.timezone
          );
          timezoneValue = tz ? tz.value.split("(")[0].trim() : formData.timezone.split(" ")[0];
        }
      }
      formDataToSend.append("timezone", timezoneValue);

      // Date and time
      const startDateTime = formatDateTime();
      if (startDateTime) formDataToSend.append("startTime", startDateTime);
      formDataToSend.append("startTimeHour", formData.startHour.toString());
      formDataToSend.append("startTimeMinute", formData.startMinute);
      formDataToSend.append("StartTimeAmPm", formData.startPeriod);

      const duration = formData.customDuration
        ? parseInt(formData.customDuration)
        : parseInt(formData.duration);
      formDataToSend.append("duration", duration.toString());
      formDataToSend.append("WebinarDuration", duration.toString());

      if (startDateTime) {
        const endDateTime = new Date(new Date(startDateTime).getTime() + duration * 60 * 1000);
        formDataToSend.append("endTime", endDateTime.toISOString());
      }

      // Recurring
      formDataToSend.append("recurringEvent", formData.recurringEvent);
      if (formData.recurringEvent) {
        formDataToSend.append("frequency", formData.frequency);
        formDataToSend.append("endDate", new Date(formData.endDate).toISOString());
      }

      if (formData.description) formDataToSend.append("description", formData.description);

      // Stage Control
      formDataToSend.append("allowStageAccess", formData.allowStageAccess);
      formDataToSend.append("allowCoHostsManage", formData.allowCoHostsManage);
      formDataToSend.append("autoSpotlight", formData.autoSpotlight);
      formDataToSend.append("preAssignPresenters", formData.preAssignPresenters);
      if (formData.preAssignPresenters && formData.presenterEmails.length > 0) {
        formDataToSend.append("emails", JSON.stringify(formData.presenterEmails));
      }
      if (formData.coHostEmails.length > 0) {
        formDataToSend.append("coHostEmails", JSON.stringify(formData.coHostEmails));
      }

      // Attendee
      formDataToSend.append("showAttendeeList", formData.showAttendeeList);
      formDataToSend.append("showAttendeeCount", formData.showAttendeeCount);
      formDataToSend.append("allowChat", formData.allowChat);
      formDataToSend.append("allowReactions", formData.allowReactions);

      // Background
      formDataToSend.append("customBackgroundEnabled", formData.customBackground);
      if (formData.customBackground && files.customBackgroundImage) {
        formDataToSend.append("backgroundImage", files.customBackgroundImage);
      }

      // Registration
      formDataToSend.append("requireRegistration", formData.requireRegistration);
      formDataToSend.append("registrationApprovalMode", formData.registrationApprovalMode);
      formDataToSend.append("registrationFields", JSON.stringify(formData.registrationFields));
      formDataToSend.append("confirmationEmailEnabled", formData.confirmationEmailEnabled);
      formDataToSend.append("confirmationEmailAdditionalCopy", formData.confirmationEmailAdditionalCopy);
      formDataToSend.append("includeCalendarFile", true);
      if (formData.requireRegistration) {
        formDataToSend.append("registrationTitle", formData.registrationTitle.trim());
        formDataToSend.append("registrationSubtitle", formData.registrationSubtitle.trim());
        if (files.registrationCoverImage) {
          formDataToSend.append("registrationCoverImage", files.registrationCoverImage);
        }
      }

      // Waiting Room & Countdown Timer
      formDataToSend.append("countdownTimer", formData.countdownTimer);
      formDataToSend.append("waitingRoom", formData.waitingRoom);
      if (formData.countdownTimer) {
        const mins = formData.countdownCustomMinutes
          ? parseInt(formData.countdownCustomMinutes)
          : parseInt(formData.countdownMinutes);
        formDataToSend.append("countdownMinutes", mins);
      }

      // Event Landing Page link (only when feature is enabled)
      if (isEventLandingLayoutEnabled) {
        if (landingPageMode === "linked" && selectedLandingPageId) {
          formDataToSend.append("eventLandingPageId", selectedLandingPageId);
        } else {
          formDataToSend.append("eventLandingPageId", "");
        }
      }

      // Recording
      formDataToSend.append("autoRecordWebinar", formData.autoRecordWebinar);
      formDataToSend.append("autoShareReplay", formData.autoShareReplay);
      if (formData.replayExpirationDate) {
        formDataToSend.append("replayExpirationDate", formData.replayExpirationDate);
      }

      // Live Streaming destinations — only persist when the user enabled them.
      // Sending empty strings when disabled clears the previously stored values
      // on edit, so the user can fully turn streaming off.
      if (formData.youtubeEnabled) {
        formDataToSend.append("streamYouTubeUrl", formData.streamYouTubeUrl || "");
        formDataToSend.append("streamYouTubeKey", formData.streamYouTubeKey || "");
        formDataToSend.append("streamYouTubeBroadcastId", formData.streamYouTubeBroadcastId || "");
      } else {
        formDataToSend.append("streamYouTubeUrl", "");
        formDataToSend.append("streamYouTubeKey", "");
        formDataToSend.append("streamYouTubeBroadcastId", "");
      }
      if (formData.facebookEnabled) {
        formDataToSend.append("streamFacebookUrl", formData.streamFacebookUrl || "");
        formDataToSend.append("streamFacebookKey", formData.streamFacebookKey || "");
      } else {
        formDataToSend.append("streamFacebookUrl", "");
        formDataToSend.append("streamFacebookKey", "");
      }
      // LinkedIn streaming UI is hidden — preserve any existing values by not sending these fields.

      let response;
      if (isEditMode && roomId) {
        response = await updateScheduledMeeting(roomId, formDataToSend);
      } else {
        response = await createScheduledMeeting(formDataToSend);
      }

      if (response && response.success !== false) {
        if (isDraft) {
          toast.success("Draft saved!");
        } else {
          toast.success(isEditMode ? "Webinar updated!" : "Webinar created!");
          // Build registration URL from response
          const meetingData = response?.data || response;
          const webinarId = meetingData?.roomId || meetingData?.id || meetingData?._id || "";
          if (webinarId && !isEditMode) {
            // If linked to an event landing page, use that URL instead
            let regUrl;
            if (isEventLandingLayoutEnabled && landingPageMode === "linked" && selectedLandingPage?.landingPageSlug) {
              regUrl = `${window.location.origin}/event/${selectedLandingPage.landingPageSlug}`;
            } else {
              regUrl = `${window.location.origin}/webinar/${webinarId}/event`;
            }
            setCreatedWebinarUrl(regUrl);
            setShowShareModal(true);
          } else {
            navigate("/dashboard/schedule");
          }
        }
      } else {
        toast.error(response?.message || "Failed to save. Please try again.");
      }
    } catch (error) {
      console.error("Webinar scheduling error:", error);
      toast.error(error.response?.data?.message || error.message || "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  const inputClass =
    "w-full px-3 py-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 text-sm";
  const selectClass =
    "w-full px-3 py-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 text-sm";

  const hostName = user?.name || user?.firstName || user?.email || "You";

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(createdWebinarUrl);
    toast.success("Link copied to clipboard!");
  };

  const goToStep = (step) => {
    if (step < 1 || step > 4) return;
    // Validate current step before going forward (skip validation in edit mode)
    if (!isEditMode && step > currentStep) {
      if (currentStep === 1) {
        const stepErrors = {};
        if (!formData.webinarTitle.trim()) stepErrors.webinarTitle = "Please enter a webinar title";
        // Room auto-assigned by backend
        if (!formData.startDate) stepErrors.startDate = "Please select a start date";
        if (Object.keys(stepErrors).length > 0) {
          setErrors(stepErrors);
          toast.error(Object.values(stepErrors)[0]);
          return;
        }
      }
    }
    setErrors({});
    setCurrentStep(step);
  };

  // ── Review helpers ───────────────────────────────────────────────
  const getSelectedRoom = () => rooms.find((r) => r.roomId === formData.roomId);
  const formatReviewDate = () => {
    if (!formData.startDate) return "Not set";
    const d = new Date(formData.startDate);
    return `${d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })} at ${formData.startHour}:${formData.startMinute} ${formData.startPeriod}`;
  };

  return (
    <div className="w-full max-w-3xl mx-auto px-4 py-8 min-h-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => currentStep > 1 ? goToStep(currentStep - 1) : navigate("/dashboard/schedule")}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-400" />
        </button>
        <h1 className="text-xl font-bold text-white">
          {isEditMode ? "Edit Webinar" : "Create Webinar"}
        </h1>
      </div>

      {/* Step Indicator */}
      <StepIndicator currentStep={currentStep} steps={STEP_LABELS} onStepClick={isEditMode ? goToStep : undefined} />

      <form onSubmit={(e) => { e.preventDefault(); if (currentStep < 4) goToStep(currentStep + 1); }} className="space-y-5">

        {/* ════════════════════════════════════════════════════════════
            STEP 1: Webinar Details
            ════════════════════════════════════════════════════════════ */}
        {currentStep === 1 && (
          <>
            {/* ── AI Assistant ─────────────────────────────────────────── */}
            <CollapsibleSection
              title="AI Assistant"
              expanded={expandedSections.aiAssistant}
              onToggle={() => toggleSection("aiAssistant")}
            >
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[#832CFF]" />
                <span className="text-white text-sm font-semibold">AI Assistant</span>
              </div>
              <input
                type="text"
                value={aiTopicInput}
                onChange={(e) => setAiTopicInput(e.target.value)}
                placeholder="What's your webinar about? (e.g. real estate marketing, fitness coaching)"
                className="w-full px-3 py-2.5 mb-3 border border-white/10 rounded-lg bg-white/5 text-white placeholder-gray-500 focus:outline-none focus:border-[#832CFF]/50 text-sm"
              />
              <div className="flex flex-wrap gap-2 mb-4">
                <button type="button" onClick={handleGenerateTitle} disabled={aiLoading.title}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#832CFF]/20 hover:bg-[#832CFF]/40 text-white text-xs rounded-full border border-[#832CFF]/40 transition-colors disabled:opacity-50">
                  {aiLoading.title ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Generate Title
                </button>
                <button type="button" onClick={handleGenerateDescription} disabled={aiLoading.description}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#832CFF]/20 hover:bg-[#832CFF]/40 text-white text-xs rounded-full border border-[#832CFF]/40 transition-colors disabled:opacity-50">
                  {aiLoading.description ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Generate Description
                </button>
                <button type="button" onClick={handleSuggestTimes} disabled={aiLoading.times}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#832CFF]/20 hover:bg-[#832CFF]/40 text-white text-xs rounded-full border border-[#832CFF]/40 transition-colors disabled:opacity-50">
                  {aiLoading.times ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3" />} Suggest Times
                </button>
                <button type="button" onClick={handleTalkingPoints} disabled={aiLoading.talkingPoints}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#832CFF]/20 hover:bg-[#832CFF]/40 text-white text-xs rounded-full border border-[#832CFF]/40 transition-colors disabled:opacity-50">
                  {aiLoading.talkingPoints ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />} Talking Points
                </button>
              </div>
              <textarea value={aiSuggestionText} onChange={(e) => setAiSuggestionText(e.target.value)}
                placeholder="AI suggestions will appear here..." rows={4}
                className="w-full px-3 py-3 border border-[#832CFF]/30 rounded-lg bg-[#1a1a2e] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 text-sm resize-none" />
              <div className="flex gap-2 mt-3">
                <button type="button" onClick={handleApplySuggestion} disabled={!aiSuggestionText}
                  className="px-4 py-1.5 bg-[#832CFF] hover:bg-[#6a1fbf] text-white text-xs rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Apply Suggestion</button>
                <button type="button" onClick={handleClearSuggestion} disabled={!aiSuggestionText}
                  className="px-4 py-1.5 bg-transparent hover:bg-white/10 text-gray-300 text-xs rounded-lg border border-gray-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Clear</button>
              </div>
            </CollapsibleSection>

            {/* ── Basic Fields ─────────────────────────────────────────── */}
            <div className="space-y-4">
              {/* Title (Public) */}
              <div>
                <label className="text-white text-sm font-medium">
                  Title (Public) <span className="text-red-400">*</span>
                </label>
                <input type="text" placeholder="Marketing Strategy Webinar" value={formData.webinarTitle}
                  onChange={(e) => handleInputChange("webinarTitle", e.target.value)} maxLength={100}
                  className={`${inputClass} mt-1 ${errors.webinarTitle ? "border-red-500 focus:ring-red-500" : ""}`} />
                {errors.webinarTitle && <p className="text-red-400 text-xs mt-1">{errors.webinarTitle}</p>}
              </div>

              {/* Internal Name */}
              <div>
                <label className="text-white text-sm font-medium">Internal Name</label>
                <p className="text-gray-500 text-xs mb-1">For your dashboard reference only</p>
                <input type="text" placeholder="e.g. Q2 Marketing Kickoff" value={formData.internalName}
                  onChange={(e) => handleInputChange("internalName", e.target.value)} maxLength={100}
                  className={`${inputClass}`} />
              </div>

              {/* Description */}
              <div>
                <label className="text-white text-sm font-medium">Description (Optional)</label>
                <textarea placeholder="What will you cover in this webinar?" value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)} maxLength={300} rows={3}
                  className={`${inputClass} mt-1 resize-none`} />
              </div>

              {/* Host (auto-filled) */}
              <div>
                <label className="text-white text-sm font-medium">Host</label>
                <div className="flex items-center gap-2 mt-1 px-3 py-3 border border-gray-600 rounded-lg bg-gray-800/30 text-gray-300 text-sm">
                  <User className="w-4 h-4 text-[#832CFF]" />
                  {hostName}
                </div>
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white text-sm font-medium">Date <span className="text-red-400">*</span></label>
                  <div className="relative mt-1">
                    <input type="text" readOnly value={formatDisplayDate(formData.startDate) || "dd-mm-yyyy"}
                      onClick={() => setShowStartCalendar((prev) => !prev)}
                      className={`${inputClass} cursor-pointer ${errors.startDate ? "border-red-500 focus:ring-red-500" : ""}`} />
                    {showStartCalendar && (
                      <div className="absolute z-50 mt-1">
                        <Calendar value={formData.startDate} minDate={new Date()} onChange={(date) => { handleInputChange("startDate", date); setShowStartCalendar(false); }} />
                      </div>
                    )}
                  </div>
                  {errors.startDate && <p className="text-red-400 text-xs mt-1">{errors.startDate}</p>}
                </div>
                <div>
                  <label className="text-white text-sm font-medium">Time <span className="text-red-400">*</span></label>
                  <div className="flex gap-2 mt-1">
                    <select value={`${formData.startHour}-${formData.startMinute}`}
                      onChange={(e) => { const [h, m] = e.target.value.split("-"); handleInputChange("startHour", parseInt(h, 10)); handleInputChange("startMinute", m); }}
                      className={selectClass} style={{ flex: 2 }}>
                      {TIME_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value} style={{ backgroundColor: "#1a1a2e", color: "#fff" }}>{opt.label}</option>
                      ))}
                    </select>
                    <select value={formData.startPeriod} onChange={(e) => handleInputChange("startPeriod", e.target.value)}
                      className={selectClass} style={{ flex: 1 }}>
                      <option value="AM" style={{ backgroundColor: "#1a1a2e", color: "#fff" }}>AM</option>
                      <option value="PM" style={{ backgroundColor: "#1a1a2e", color: "#fff" }}>PM</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Duration */}
              <div>
                <label className="text-white text-sm font-medium">Duration (minutes) <span className="text-red-400">*</span></label>
                <input type="number" value={formData.duration} onChange={(e) => handleInputChange("duration", e.target.value)}
                  min="5" max="480" className={`${inputClass} mt-1`} />
              </div>

              {/* Timezone */}
              <div>
                <label className="text-white text-sm font-medium">Timezone</label>
                <select value={formData.timezone} onChange={(e) => handleInputChange("timezone", e.target.value)} className={`${selectClass} mt-1`}>
                  {timezones.map((tz) => (
                    <option key={tz.value} value={tz.value} style={{ backgroundColor: "#1a1a2e", color: "#fff" }}>{tz.label}</option>
                  ))}
                </select>
              </div>

              {/* Auto-Record toggle */}
              <div className="space-y-4 pt-2">
                <div className="flex items-center justify-between p-3 rounded-lg border border-white/10 bg-white/5">
                  <div>
                    <div className="text-white text-sm font-medium">Auto-Record</div>
                    <div className="text-gray-400 text-xs mt-0.5">Automatically start recording</div>
                  </div>
                  <ToggleSwitch enabled={formData.autoRecordWebinar}
                    onToggle={() => handleInputChange("autoRecordWebinar", !formData.autoRecordWebinar)} />
                </div>
              </div>
            </div>

            {/* ── Stage Control ─────────────────────────────────────── */}
            <CollapsibleSection title="Stage Control" expanded={expandedSections.stageControl} onToggle={() => toggleSection("stageControl")}>
              <div className="space-y-3">
                <PurpleCheckbox checked={formData.allowStageAccess} onChange={(v) => handleInputChange("allowStageAccess", v)}
                  label="Allow Stage Access" description="Attendees can ask to join the stage" />
                <PurpleCheckbox checked={formData.allowCoHostsManage} onChange={(v) => handleInputChange("allowCoHostsManage", v)}
                  label="Co-hosts Manage Stage" description="Co-hosts can approve, deny, or remove" />
                <PurpleCheckbox checked={formData.autoSpotlight} onChange={(v) => handleInputChange("autoSpotlight", v)}
                  label="Auto Spotlight" description="Automatically switch view to active speaker" />
              </div>
              <div className="border-t border-white/10 pt-4 mt-4">
                <label className="text-white text-sm font-medium">Pre-assign Presenters (Optional)</label>
                <p className="text-gray-400 text-xs mb-2">Add emails to auto-join stage</p>
                <div className="flex gap-2">
                  <input type="email" placeholder="presenter@email.com" value={formData.presenterEmailInput}
                    onChange={(e) => handleInputChange("presenterEmailInput", e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handlePresenterEmailAdd())}
                    className={`${inputClass} flex-1`} />
                  <button type="button" onClick={handlePresenterEmailAdd}
                    className="px-4 py-2 bg-[#832CFF] hover:bg-[#6a1fbf] text-white text-sm rounded-lg transition-colors">Add</button>
                </div>
                {formData.presenterEmails.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.presenterEmails.map((email) => (
                      <span key={email} className="flex items-center gap-1 bg-[#832CFF]/20 text-purple-300 px-2.5 py-1 rounded-md text-xs">
                        {email}
                        <button type="button" onClick={() => handlePresenterEmailRemove(email)} className="hover:text-white"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="border-t border-white/10 pt-4 mt-4">
                <label className="text-white text-sm font-medium">Assign Co-Hosts (Optional)</label>
                <p className="text-gray-400 text-xs mb-2">Add emails to assign co-host role</p>
                <div className="flex gap-2">
                  <input type="email" placeholder="cohost@email.com" value={formData.coHostEmailInput}
                    onChange={(e) => handleInputChange("coHostEmailInput", e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleCoHostEmailAdd())}
                    className={`${inputClass} flex-1`} />
                  <button type="button" onClick={handleCoHostEmailAdd}
                    className="px-4 py-2 bg-[#832CFF] hover:bg-[#6a1fbf] text-white text-sm rounded-lg transition-colors">Add</button>
                </div>
                {formData.coHostEmails.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.coHostEmails.map((email) => (
                      <span key={email} className="flex items-center gap-1 bg-[#00c853]/20 text-green-300 px-2.5 py-1 rounded-md text-xs">
                        {email}
                        <button type="button" onClick={() => handleCoHostEmailRemove(email)} className="hover:text-white"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </CollapsibleSection>

            {/* ── Attendee Options ──────────────────────────────────── */}
            <CollapsibleSection title="Attendee Options" expanded={expandedSections.attendeeOptions} onToggle={() => toggleSection("attendeeOptions")}>
              <div className="space-y-3">
                <PurpleCheckbox checked={formData.showAttendeeList} onChange={(v) => handleInputChange("showAttendeeList", v)}
                  label="Show Attendee List" description="Attendees can see who's connected" />
                <PurpleCheckbox checked={formData.showAttendeeCount} onChange={(v) => handleInputChange("showAttendeeCount", v)}
                  label="Show Attendee Count" description="Display total number of viewers" />
                <PurpleCheckbox checked={formData.allowChat} onChange={(v) => handleInputChange("allowChat", v)}
                  label="Chat" description="Enable attendee chat" />
                <PurpleCheckbox checked={formData.allowReactions} onChange={(v) => handleInputChange("allowReactions", v)}
                  label="Reactions" description="Allow emoji reactions" />
                <PurpleCheckbox checked={formData.waitingRoom} onChange={(v) => handleInputChange("waitingRoom", v)}
                  label="Waiting Room" description="Enable a waiting room before the webinar begins" />
              </div>
            </CollapsibleSection>

            {/* ── Customizations ────────────────────────────────────── */}
            <CollapsibleSection title="Customizations" expanded={expandedSections.customizations} onToggle={() => toggleSection("customizations")}>
              <div className="space-y-4">
                <PurpleCheckbox checked={formData.customBackground} onChange={(v) => handleInputChange("customBackground", v)}
                  label="Custom Background" description="Personalize backdrop for branded meeting space" />
                {formData.customBackground && (
                  <UploadArea onFileSelect={(file) => handleFileSelect("customBackgroundImage", file)}
                    onRemove={() => handleFileRemove("customBackgroundImage")}
                    file={files.customBackgroundImage} existingUrl={existingImageUrls.customBackgroundImage} recommendedText="1366x1100 recommended" />
                )}
                <PurpleCheckbox checked={formData.countdownTimer} onChange={(v) => handleInputChange("countdownTimer", v)}
                  label="Countdown Timer" description="Enable countdown timer in the Event Landing Page" />
              </div>
            </CollapsibleSection>

            {/* ── Live Streaming ────────────────────────────────────── */}
            <CollapsibleSection title="Live Streaming" expanded={expandedSections.liveStreaming} onToggle={() => toggleSection("liveStreaming")}>
              <p className="text-gray-400 text-xs mb-3">Simulcast this webinar to YouTube or Facebook via RTMP.</p>
              <div className="space-y-3">
                {/* YouTube */}
                <div className="border border-white/10 rounded-lg p-4 bg-white/5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-red-600 flex items-center justify-center">
                        <Youtube className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm font-medium text-white">YouTube</span>
                    </div>
                    <ToggleSwitch enabled={formData.youtubeEnabled}
                      onToggle={() => handleInputChange("youtubeEnabled", !formData.youtubeEnabled)} />
                  </div>
                  {formData.youtubeEnabled && (
                    <div className="space-y-2 mt-3">
                      <input
                        type="text"
                        placeholder="RTMP URL (e.g., rtmp://a.rtmp.youtube.com/live2)"
                        value={formData.streamYouTubeUrl}
                        onChange={(e) => handleInputChange("streamYouTubeUrl", e.target.value)}
                        className={inputClass}
                      />
                      <input
                        type="password"
                        placeholder="Stream Key"
                        value={formData.streamYouTubeKey}
                        onChange={(e) => handleInputChange("streamYouTubeKey", e.target.value)}
                        className={inputClass}
                      />
                      <input
                        type="text"
                        placeholder="Broadcast ID (optional)"
                        value={formData.streamYouTubeBroadcastId}
                        onChange={(e) => handleInputChange("streamYouTubeBroadcastId", e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  )}
                </div>

                {/* Facebook */}
                <div className="border border-white/10 rounded-lg p-4 bg-white/5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-blue-600 flex items-center justify-center">
                        <Facebook className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm font-medium text-white">Facebook</span>
                    </div>
                    <ToggleSwitch enabled={formData.facebookEnabled}
                      onToggle={() => handleInputChange("facebookEnabled", !formData.facebookEnabled)} />
                  </div>
                  {formData.facebookEnabled && (
                    <div className="space-y-2 mt-3">
                      <input
                        type="text"
                        placeholder="RTMP URL (e.g., rtmps://live-api-s.facebook.com:443/rtmp/)"
                        value={formData.streamFacebookUrl}
                        onChange={(e) => handleInputChange("streamFacebookUrl", e.target.value)}
                        className={inputClass}
                      />
                      <input
                        type="password"
                        placeholder="Stream Key"
                        value={formData.streamFacebookKey}
                        onChange={(e) => handleInputChange("streamFacebookKey", e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  )}
                </div>

                {/* LinkedIn — hidden for now */}
              </div>
            </CollapsibleSection>
          </>
        )}

        {/* ════════════════════════════════════════════════════════════
            STEP 2: Event Landing Page Setup
            ════════════════════════════════════════════════════════════ */}
        {currentStep === 2 && (
          <div className="space-y-5">
            {/* Landing Page Source Selection — only visible when feature flag is enabled */}
            {isEventLandingLayoutEnabled && (
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Event Landing Page Source</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => { setLandingPageMode("builtin"); setSelectedLandingPageId(""); setSelectedLandingPage(null); }}
                  className={`p-3 rounded-xl border text-left transition-all ${landingPageMode === "builtin"
                    ? "border-[#832CFF] bg-[#832CFF]/10 ring-1 ring-[#832CFF]/40"
                    : "border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20"
                    }`}
                >
                  <div className="text-white text-sm font-semibold">Built-in Landing Page</div>
                  <div className="text-gray-400 text-xs mt-1">Use the webinar's default registration page</div>
                </button>
                <button
                  type="button"
                  onClick={() => setLandingPageMode("linked")}
                  className={`p-3 rounded-xl border text-left transition-all ${landingPageMode === "linked"
                    ? "border-[#832CFF] bg-[#832CFF]/10 ring-1 ring-[#832CFF]/40"
                    : "border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20"
                    }`}
                >
                  <div className="text-white text-sm font-semibold">Event Landing Page</div>
                  <div className="text-gray-400 text-xs mt-1">Link a custom event landing page with layouts</div>
                </button>
              </div>
            </div>
            )}

            {/* Event Landing Page Selector — shown when "linked" mode is selected */}
            {isEventLandingLayoutEnabled && landingPageMode === "linked" && (
              <div className="border border-[#832CFF]/30 rounded-xl p-4 bg-[#832CFF]/5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white text-sm font-semibold">Select Event Landing Page</div>
                    <div className="text-gray-400 text-xs mt-0.5">Choose an existing page or create a new one</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCreateLandingPageModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white bg-[#832CFF] hover:bg-[#6a1fbf] rounded-lg transition-colors"
                  >
                    <Plus className="w-3 h-3" /> Create New
                  </button>
                </div>

                {loadingLandingPages ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
                  </div>
                ) : eventLandingPages.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-gray-400 text-sm">No event landing pages found.</p>
                    <button
                      type="button"
                      onClick={() => setShowCreateLandingPageModal(true)}
                      className="mt-2 text-[#832CFF] text-sm hover:underline"
                    >
                      Create your first event landing page
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {eventLandingPages.map((page) => {
                      const pageId = page.id || page._id;
                      const isSelected = selectedLandingPageId === pageId;
                      return (
                        <button
                          type="button"
                          key={pageId}
                          onClick={() => handleSelectLandingPage(pageId)}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${isSelected
                            ? "border-[#832CFF] bg-[#832CFF]/15"
                            : "border-white/10 bg-white/5 hover:bg-white/10"
                            }`}
                        >
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? "border-[#832CFF] bg-[#832CFF]" : "border-gray-500"}`}>
                            {isSelected && (
                              <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                                <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-white text-sm font-medium truncate">{page.yourName}</div>
                            <div className="text-gray-400 text-xs mt-0.5 flex items-center gap-2">
                              <span className="capitalize">{page.layoutType?.replace(/-/g, " ") || "Classic"}</span>
                              <span className="text-gray-600">|</span>
                              <span className="capitalize">{page.format || "Live"}</span>
                              {page.scheduledStartTime && (
                                <>
                                  <span className="text-gray-600">|</span>
                                  <span>{new Date(page.scheduledStartTime).toLocaleDateString()}</span>
                                </>
                              )}
                            </div>
                          </div>
                          {isSelected && page.landingPageSlug && (
                            <a
                              href={`/event/${page.landingPageSlug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex-shrink-0 text-[#832CFF] hover:text-white"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Selected page summary */}
                {selectedLandingPage && (
                  <div className="mt-3 p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center gap-2 mb-1">
                      <Check className="w-4 h-4 text-green-400" />
                      <span className="text-green-400 text-xs font-semibold uppercase tracking-wider">Linked</span>
                    </div>
                    <div className="text-white text-sm font-medium">{selectedLandingPage.yourName}</div>
                    {selectedLandingPage.landingPageSlug && (
                      <div className="text-gray-400 text-xs mt-1">
                        URL: {window.location.origin}/event/{selectedLandingPage.landingPageSlug}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Built-in landing page settings — always show when feature flag is off, or when builtin mode selected */}
            {(!isEventLandingLayoutEnabled || landingPageMode === "builtin") && (
            <>
            {/* Hero Image Upload */}
            <div>
              <label className="text-white text-sm font-medium flex items-center gap-2">
                <Image className="w-4 h-4 text-[#832CFF]" /> Hero Image
              </label>
              <p className="text-gray-500 text-xs mb-2">This image appears on your event landing page. Recommended size: 1366x1100.</p>
              <UploadArea
                onFileSelect={(file) => handleFileSelect("registrationHeroImage", file)}
                onRemove={() => handleFileRemove("registrationHeroImage")}
                file={files.registrationHeroImage}
                existingUrl={existingImageUrls.registrationHeroImage}
                recommendedText="1366x1100 minimum recommended"
              />
            </div>

            {/* Event Landing Page Preview */}
            <div className="border border-white/10 rounded-xl p-4 bg-white/5">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="w-4 h-4 text-[#832CFF]" />
                <span className="text-white text-sm font-semibold">Event Landing Page Preview</span>
              </div>
              {/* Mimics the actual event landing page split layout */}
              <div className="bg-[#0D0D0D] rounded-xl overflow-hidden border border-white/10">
                {/* Mini Header */}
                <div className="flex items-center justify-between h-8 px-3 border-b border-white/10 bg-black/50">
                  <span className="text-white text-[9px] font-bold tracking-wider">R-LINK</span>
                  <span className="text-gray-500 text-[8px] uppercase tracking-wider">Event Landing Page</span>
                </div>
                {/* Split Layout */}
                <div className="flex flex-col sm:flex-row min-h-[280px]">
                  {/* Left Panel - Hero Image */}
                  <div className="w-full sm:w-1/2 bg-black flex items-center justify-center">
                    {(files.registrationHeroImage || existingImageUrls.registrationHeroImage) ? (
                      <img
                        src={files.registrationHeroImage ? URL.createObjectURL(files.registrationHeroImage) : existingImageUrls.registrationHeroImage}
                        alt="Hero"
                        className="w-full h-full object-cover min-h-[140px] sm:min-h-[280px]"
                      />
                    ) : (
                      <div className="w-full h-full min-h-[140px] sm:min-h-[280px] flex items-center justify-center" style={{ background: "linear-gradient(135deg, #832CFF40, #0D0D0D)" }}>
                        <div className="text-center p-4">
                          <div className="w-12 h-12 mx-auto mb-2 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#832CFF20" }}>
                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#832CFF" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Right Panel - Form */}
                  <div className="w-full sm:w-1/2 p-4 flex flex-col justify-center">
                    <h3 className="text-white font-bold text-sm mb-1">{formData.webinarTitle || "Webinar Title"}</h3>
                    {formData.description && (
                      <p className="text-gray-400 text-[10px] mb-3 line-clamp-2">{formData.description}</p>
                    )}
                    {/* Event Time */}
                    <div className="mb-3 p-2 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex items-center gap-1 mb-0.5">
                        <Clock className="w-3 h-3 text-purple-400" />
                        <span className="text-purple-400 text-[8px] font-medium uppercase tracking-wider">Event Time</span>
                      </div>
                      <p className="text-white text-[10px] font-medium">{formatReviewDate()}</p>
                    </div>
                    {/* Form Fields */}
                    <div className="space-y-1.5">
                      {formData.registrationFields.filter(f => f.enabled).map((f) => (
                        <div key={f.name}>
                          <span className="text-gray-300 text-[9px] font-medium">{f.name} {f.required && <span className="text-red-400">*</span>}</span>
                          <div className="mt-0.5 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-gray-500 text-[9px]">{f.name === "Email Address" ? "john@example.com" : f.name === "Phone Number" ? "+1 (555) 000-0000" : f.name === "First Name" ? "John" : f.name === "Last Name" ? "Doe" : f.name}</div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-[#832CFF] text-white text-center py-2 rounded-xl text-[10px] font-semibold mt-3">Register Now</div>
                  </div>
                </div>
                {/* Mini Footer */}
                <div className="flex items-center justify-center py-1.5 border-t border-white/10">
                  <span className="text-gray-600 text-[8px]">Powered by <span className="text-gray-400 font-semibold">R-Link</span></span>
                </div>
              </div>
            </div>

            {/* Registration Required Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border border-white/10 bg-white/5">
              <div>
                <div className="text-white text-sm font-medium">Registration Required</div>
                <div className="text-gray-400 text-xs mt-0.5">Attendees must register before joining</div>
              </div>
              <ToggleSwitch enabled={formData.requireRegistration}
                onToggle={() => handleInputChange("requireRegistration", !formData.requireRegistration)} />
            </div>

            {/* Registration Fields - only shown when registration is required */}
            {formData.requireRegistration && (
              <>
                {/* Fixed Registration Fields */}
                <div>
                  <label className="text-white text-sm font-medium">Registration Form Fields</label>
                  <div className="space-y-2 mt-2">
                    {formData.registrationFields.map((field) => {
                      const isCoreField = ["First Name", "Last Name", "Email Address"].includes(field.name);
                      return (
                        <div key={field.name} className="flex items-center justify-between bg-[#832CFF]/15 px-3 py-2 rounded-lg">
                          <div className="flex items-center gap-2">
                            <PurpleCheckbox checked={field.enabled} onChange={() => { }} label={field.name} />
                          </div>
                          <div className="flex items-center gap-2">
                            {isCoreField ? (
                              <span className="flex items-center gap-1.5">
                                <span className="w-4 h-4 rounded border-2 bg-[#832CFF] border-[#832CFF] flex items-center justify-center">
                                  <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none">
                                    <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </span>
                                <span className="text-xs text-gray-400">Required</span>
                              </span>
                            ) : (
                              <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                <input type="checkbox" checked={field.required} onChange={() => toggleRegistrationFieldRequired(field.name)} className="sr-only" />
                                <span className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${field.required ? "bg-[#832CFF] border-[#832CFF]" : "bg-transparent border-gray-500"
                                  }`}>
                                  {field.required && (
                                    <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none">
                                      <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  )}
                                </span>
                                <span className="text-xs text-gray-400">Required</span>
                              </label>
                            )}
                            {!isCoreField && (
                              <button type="button" onClick={() => removeRegistrationField(field.name)} className="text-gray-400 hover:text-red-400 ml-1">
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {["Company", "Job Title", "Phone Number", "LinkedIn Profile", "Questions/Comments"]
                      .filter((f) => !formData.registrationFields.some((rf) => rf.name === f))
                      .map((f) => (
                        <button key={f} type="button" onClick={() => addRegistrationField(f)}
                          className="text-xs text-gray-300 px-2.5 py-1 rounded-full border border-gray-600 hover:border-purple-500 hover:text-white transition-colors">
                          + {f}
                        </button>
                      ))}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <input type="text" placeholder="Custom field name..." value={customFieldInput}
                      onChange={(e) => setCustomFieldInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomField())}
                      className={`${inputClass} flex-1`} />
                    <button type="button" onClick={addCustomField}
                      className="px-4 py-2 bg-[#832CFF] hover:bg-[#6a1fbf] text-white text-sm rounded-lg transition-colors">Add</button>
                  </div>
                </div>

                {/* Toggles */}
                <div className="space-y-4 pt-2">
                  <div className="flex items-center justify-between p-3 rounded-lg border border-white/10 bg-white/5">
                    <div>
                      <div className="text-white text-sm font-medium">Require registration to watch replay</div>
                      <div className="text-gray-400 text-xs mt-0.5">Viewers must register to access the replay</div>
                    </div>
                    <ToggleSwitch enabled={formData.requireRegistrationForReplay}
                      onToggle={() => handleInputChange("requireRegistrationForReplay", !formData.requireRegistrationForReplay)} />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border border-white/10 bg-white/5">
                    <div>
                      <div className="text-white text-sm font-medium flex items-center gap-2">
                        <Lock className="w-4 h-4" /> Add 4-digit passcode
                      </div>
                      <div className="text-gray-400 text-xs mt-0.5">Extra security for the event landing page</div>
                    </div>
                    <ToggleSwitch enabled={formData.requirePasscode}
                      onToggle={() => handleInputChange("requirePasscode", !formData.requirePasscode)} />
                  </div>
                  {formData.requirePasscode && (
                    <div>
                      <label className="text-white text-xs font-medium">4-digit Passcode</label>
                      <input type="text" placeholder="1234" value={formData.passcode}
                        onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 4); handleInputChange("passcode", v); }}
                        maxLength={4} className={`${inputClass} mt-1 w-32 tracking-widest text-center text-lg`} />
                    </div>
                  )}
                </div>

                {/* Approval Mode */}
                <div>
                  <label className="text-white text-xs font-medium">Approval Mode</label>
                  <select value={formData.registrationApprovalMode} onChange={(e) => handleInputChange("registrationApprovalMode", e.target.value)}
                    className={`${selectClass} mt-1`}>
                    <option value="auto" style={{ backgroundColor: "#1a1a2e", color: "#fff" }}>Auto-Approve Registrations</option>
                    <option value="manual" style={{ backgroundColor: "#1a1a2e", color: "#fff" }}>Manual Approval</option>
                  </select>
                  <p className="text-gray-500 text-xs mt-1">
                    {formData.registrationApprovalMode === "auto"
                      ? "Registrants will be automatically approved and receive confirmation"
                      : "Each registration requires manual approval by the host"}
                  </p>
                </div>
              </>
            )}
            </>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════
            STEP 3: Email Settings
            ════════════════════════════════════════════════════════════ */}
        {currentStep === 3 && (
          <div className="space-y-5">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-5 h-5 text-[#832CFF]" />
              <span className="text-white text-lg font-semibold">Email Settings</span>
            </div>
            <p className="text-gray-400 text-sm">Choose which emails are sent to registrants automatically.</p>

            {/* ── Sender Settings ──────────────────────────────────── */}
            <div className="border border-white/10 rounded-xl p-4 bg-white/5 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <User className="w-4 h-4 text-[#832CFF]" />
                <span className="text-white text-sm font-semibold">Sender Settings</span>
              </div>
              <p className="text-gray-500 text-xs">Customize how your webinar emails appear to registrants.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-white text-xs font-medium">From Name</label>
                  <input type="text" placeholder="e.g. R-Link Webinars" value={formData.emailFromName}
                    onChange={(e) => handleInputChange("emailFromName", e.target.value)}
                    className={`${inputClass} mt-1`} />
                  <p className="text-gray-500 text-[10px] mt-1">Name shown in the "From" field of emails</p>
                </div>
                <div>
                  <label className="text-white text-xs font-medium">Reply-To Email</label>
                  <input type="email" placeholder="e.g. webinars@yourcompany.com" value={formData.emailReplyTo}
                    onChange={(e) => handleInputChange("emailReplyTo", e.target.value)}
                    className={`${inputClass} mt-1`} />
                  <p className="text-gray-500 text-[10px] mt-1">Where replies from registrants will be sent</p>
                </div>
              </div>
            </div>

            {/* ── Confirmation Email ─────────────────────────────── */}
            <div className="border border-white/10 rounded-xl bg-white/5 overflow-hidden">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#832CFF]/20 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-[#832CFF]" />
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">Confirmation Email</div>
                    <div className="text-gray-400 text-xs mt-0.5">Sent immediately after registration</div>
                  </div>
                </div>
                <ToggleSwitch enabled={formData.confirmationEmailEnabled}
                  onToggle={() => handleInputChange("confirmationEmailEnabled", !formData.confirmationEmailEnabled)} />
              </div>
              {formData.confirmationEmailEnabled && (
                <div className="px-4 pb-4 space-y-3 border-t border-white/5 pt-3">
                  <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3">
                    <p className="text-gray-500 text-[10px] uppercase tracking-wider font-medium mb-2">Email Preview</p>
                    <p className="text-gray-300 text-xs leading-relaxed">
                      Registrants will receive a confirmation with the webinar title, date, time, join link, and a calendar file (.ics) attachment.
                    </p>
                  </div>
                  <div>
                    <label className="text-white text-xs font-medium">Additional Message <span className="text-gray-500 font-normal">(optional)</span></label>
                    <p className="text-gray-500 text-[10px] mt-0.5 mb-1">This will appear above the "Important" section in the confirmation email.</p>
                    <textarea
                      value={formData.confirmationEmailAdditionalCopy}
                      onChange={(e) => handleInputChange("confirmationEmailAdditionalCopy", e.target.value)}
                      placeholder="e.g. Make sure to have your questions ready! We'll have a live Q&A at the end."
                      rows={3}
                      className={`${inputClass} mt-1 resize-none`}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ── 24-Hour Reminder ───────────────────────────────── */}
            <div className="border border-white/10 rounded-xl bg-white/5 overflow-hidden">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#832CFF]/20 flex items-center justify-center">
                    <Bell className="w-4 h-4 text-[#832CFF]" />
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">24-Hour Reminder</div>
                    <div className="text-gray-400 text-xs mt-0.5">Sent 24 hours before the webinar</div>
                  </div>
                </div>
                <ToggleSwitch enabled={formData.reminderEmail24h}
                  onToggle={() => handleInputChange("reminderEmail24h", !formData.reminderEmail24h)} />
              </div>
            </div>

            {/* ── 1-Hour Reminder ────────────────────────────────── */}
            <div className="border border-white/10 rounded-xl bg-white/5 overflow-hidden">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#832CFF]/20 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-[#832CFF]" />
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">1-Hour Reminder</div>
                    <div className="text-gray-400 text-xs mt-0.5">Sent 1 hour before the webinar</div>
                  </div>
                </div>
                <ToggleSwitch enabled={formData.reminderEmail1h}
                  onToggle={() => handleInputChange("reminderEmail1h", !formData.reminderEmail1h)} />
              </div>
            </div>

            {/* Reminder Additional Copy (shared for both reminders) */}
            {(formData.reminderEmail24h || formData.reminderEmail1h) && (
              <div className="border border-white/10 rounded-xl bg-white/5 p-4 space-y-3">
                <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3">
                  <p className="text-gray-500 text-[10px] uppercase tracking-wider font-medium mb-2">Reminder Email Preview</p>
                  <p className="text-gray-300 text-xs leading-relaxed">
                    Registrants will receive a reminder with the webinar title, date, time, and join link.
                  </p>
                </div>
                <div>
                  <label className="text-white text-xs font-medium">Additional Message <span className="text-gray-500 font-normal">(optional)</span></label>
                  <p className="text-gray-500 text-[10px] mt-0.5 mb-1">This will be added to the reminder emails.</p>
                  <textarea
                    value={formData.reminderEmailAdditionalCopy}
                    onChange={(e) => handleInputChange("reminderEmailAdditionalCopy", e.target.value)}
                    placeholder="e.g. Don't forget to test your audio and video before joining!"
                    rows={3}
                    className={`${inputClass} mt-1 resize-none`}
                  />
                </div>
              </div>
            )}

            {/* ── Replay Email ───────────────────────────────────── */}
            <div className="flex items-center justify-between p-4 rounded-xl border border-white/10 bg-white/5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#832CFF]/20 flex items-center justify-center">
                  <Share2 className="w-4 h-4 text-[#832CFF]" />
                </div>
                <div>
                  <div className="text-white text-sm font-medium">Replay Email</div>
                  <div className="text-gray-400 text-xs mt-0.5">Sent after the webinar with replay link (if replay enabled)</div>
                </div>
              </div>
              <ToggleSwitch enabled={formData.replayEmailEnabled}
                onToggle={() => handleInputChange("replayEmailEnabled", !formData.replayEmailEnabled)} />
            </div>

            {/* Replay Expiration Date */}
            <div className="pt-2">
              <label className="text-white text-sm font-medium">Replay Expiration Date <span className="text-gray-500 text-xs font-normal">(optional)</span></label>
              <p className="text-gray-500 text-xs mb-2">Set a date after which the replay will no longer be accessible. Leave empty for no expiration.</p>
              <input type="date" value={formData.replayExpirationDate}
                onChange={(e) => handleInputChange("replayExpirationDate", e.target.value)}
                className={`${inputClass} mt-1 w-56`}
                min={new Date().toISOString().split('T')[0]}
              />
              {formData.replayExpirationDate && (
                <button type="button" onClick={() => handleInputChange("replayExpirationDate", "")}
                  className="ml-2 text-xs text-gray-400 hover:text-red-400 transition-colors">Clear</button>
              )}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════
            STEP 4: Review & Publish
            ════════════════════════════════════════════════════════════ */}
        {currentStep === 4 && (
          <div className="space-y-5">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-5 h-5 text-[#832CFF]" />
              <span className="text-white text-lg font-semibold">Review & Publish</span>
            </div>
            <p className="text-gray-400 text-sm">Review your webinar settings before publishing.</p>

            {/* Share Section (edit mode) */}
            {isEditMode && createdWebinarUrl && (
              <div className="border border-[#832CFF]/30 rounded-xl p-4 bg-[#832CFF]/5 space-y-4">
                <div className="flex items-center gap-2">
                  <Share2 className="w-4 h-4 text-[#832CFF]" />
                  <span className="text-[#832CFF] text-xs font-semibold uppercase tracking-wider">Share Your Webinar</span>
                </div>
                <div>
                  <label className="text-white text-xs font-medium">Registration URL</label>
                  <div className="flex gap-2 mt-1">
                    <input type="text" readOnly value={createdWebinarUrl}
                      className="flex-1 px-3 py-2.5 border border-gray-600 rounded-lg bg-gray-800/50 text-white text-sm" />
                    <button type="button" onClick={handleCopyUrl}
                      className="px-4 py-2.5 bg-[#832CFF] hover:bg-[#6a1fbf] text-white rounded-lg transition-colors flex items-center gap-2 text-sm">
                      <Copy className="w-4 h-4" /> Copy
                    </button>
                  </div>
                </div>
                {/* Social Share Buttons */}
                <div>
                  <label className="text-white text-xs font-medium mb-2 block">Share on</label>
                  <SocialShareButtons url={createdWebinarUrl} title={formData.webinarTitle} formData={formData} />
                </div>
                <CalendarShareDropdown
                  label="Choose your calendar"
                  title={formData.webinarTitle || "Webinar"}
                  description={(() => {
                    const raw = formData.webinarDescription
                      ? formData.webinarDescription.replace(/<[^>]*>/g, "").trim()
                      : (formData.description || "").trim();
                    return (raw + (createdWebinarUrl ? `\n\n${createdWebinarUrl}` : "")).trim();
                  })()}
                  startTime={getCalendarStartUtc()}
                  durationMinutes={formData.customDuration ? parseInt(formData.customDuration) : parseInt(formData.duration)}
                  location={createdWebinarUrl || ""}
                  filename={formData.webinarTitle ? formData.webinarTitle.replace(/\s+/g, "-") : "webinar"}
                />
              </div>
            )}

            {/* Copy Invitation — only show when webinar already exists */}
            {isEditMode && <div className="border border-white/10 rounded-xl p-4 bg-white/5 space-y-3">
              <div className="flex items-center gap-2">
                <Copy className="w-4 h-4 text-[#832CFF]" />
                <span className="text-[#832CFF] text-xs font-semibold uppercase tracking-wider">Copy Invitation</span>
              </div>
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 text-sm text-gray-300 whitespace-pre-line">
                {(() => {
                  const tzAbbr = (() => { try { const tzId = formData.timezone?.split("(")[0].trim(); if (!tzId) return ""; const parts = new Date().toLocaleTimeString("en-US", { timeZone: tzId, timeZoneName: "short" }).split(" "); return parts[parts.length - 1]; } catch { return ""; } })();
                  const dateStr = formData.startDate ? new Date(formData.startDate).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "TBD";
                  const timeStr = `${formData.startHour}:${formData.startMinute} ${formData.startPeriod}${tzAbbr ? ` ${tzAbbr}` : ""}`;
                  const dur = formData.customDuration || formData.duration || "";
                  const desc = formData.webinarDescription ? formData.webinarDescription.replace(/<[^>]*>/g, "").trim() : "";
                  const regUrl = createdWebinarUrl || `${window.location.origin}/webinar/${formData.roomId || "..."}/event`;
                  const lines = [
                    `You're Invited to a Live Webinar!`,
                    ``,
                    `${formData.webinarTitle || "Untitled Webinar"}`,
                    ``,
                    `Date: ${dateStr}`,
                    `Time: ${timeStr}`,
                    dur ? `Duration: ${dur} minutes` : "",
                    desc ? `\n${desc.substring(0, 300)}${desc.length > 300 ? "..." : ""}` : "",
                    ``,
                    `Register here: ${regUrl}`,
                    ``,
                    `Seats are limited — reserve your spot today!`,
                  ];
                  return lines.filter(Boolean).join("\n");
                })()}
              </div>
              <button type="button" onClick={() => {
                const tzAbbr = (() => { try { const tzId = formData.timezone?.split("(")[0].trim(); if (!tzId) return ""; const parts = new Date().toLocaleTimeString("en-US", { timeZone: tzId, timeZoneName: "short" }).split(" "); return ` ${parts[parts.length - 1]}`; } catch { return ""; } })();
                const dateStr = formData.startDate ? new Date(formData.startDate).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "TBD";
                const timeStr = `${formData.startHour}:${formData.startMinute} ${formData.startPeriod}${tzAbbr ? ` ${tzAbbr}` : ""}`;
                const dur = formData.customDuration || formData.duration || "";
                const desc = formData.webinarDescription ? formData.webinarDescription.replace(/<[^>]*>/g, "").trim() : "";
                const regUrl = createdWebinarUrl || `${window.location.origin}/webinar/${formData.roomId || "..."}/event`;
                const lines = [
                  `You're Invited to a Live Webinar!`,
                  ``,
                  `${formData.webinarTitle || "Untitled Webinar"}`,
                  ``,
                  `Date: ${dateStr}`,
                  `Time: ${timeStr}`,
                  dur ? `Duration: ${dur} minutes` : "",
                  desc ? `\n${desc.substring(0, 300)}${desc.length > 300 ? "..." : ""}` : "",
                  ``,
                  `Register here: ${regUrl}`,
                  ``,
                  `Seats are limited — reserve your spot today!`,
                ];
                navigator.clipboard.writeText(lines.filter(Boolean).join("\n"));
                toast.success("Invitation copied to clipboard!");
              }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#832CFF] hover:bg-[#6a1fbf] text-white rounded-lg transition-colors text-sm font-medium">
                <Copy className="w-4 h-4" /> Copy Invitation
              </button>
            </div>}

            {/* Summary Cards */}
            <div className="space-y-3">
              {/* Webinar Details */}
              <div className="border border-white/10 rounded-xl p-4 bg-white/5 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[#832CFF] text-xs font-semibold uppercase tracking-wider">Webinar Details</span>
                  <button type="button" onClick={() => goToStep(1)} className="text-[#832CFF] text-xs hover:underline">Edit</button>
                </div>
                <div className="grid grid-cols-2 gap-y-2 text-sm">
                  <span className="text-gray-400">Title</span>
                  <span className="text-white">{formData.webinarTitle || "—"}</span>
                  {formData.internalName && (<><span className="text-gray-400">Internal Name</span><span className="text-white">{formData.internalName}</span></>)}
                  <span className="text-gray-400">Date & Time</span>
                  <span className="text-white">{formatReviewDate()}</span>
                  <span className="text-gray-400">Duration</span>
                  <span className="text-white">{formData.customDuration || formData.duration} min</span>
                  {isEditMode && <><span className="text-gray-400">Room</span>
                  <span className="text-white">{getSelectedRoom()?.roomName || formData.roomId || "—"}</span></>}
                  <span className="text-gray-400">Host</span>
                  <span className="text-white">{hostName}</span>
                </div>
              </div>

              {/* Event Landing Page */}
              <div className="border border-white/10 rounded-xl p-4 bg-white/5 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[#832CFF] text-xs font-semibold uppercase tracking-wider">Event Landing Page</span>
                  <button type="button" onClick={() => goToStep(2)} className="text-[#832CFF] text-xs hover:underline">Edit</button>
                </div>
                {isEventLandingLayoutEnabled && landingPageMode === "linked" && selectedLandingPage ? (
                  <div className="grid grid-cols-2 gap-y-2 text-sm">
                    <span className="text-gray-400">Type</span>
                    <span className="text-white flex items-center gap-1.5">
                      <ExternalLink className="w-3 h-3 text-[#832CFF]" /> Linked Event Landing Page
                    </span>
                    <span className="text-gray-400">Page Name</span>
                    <span className="text-white">{selectedLandingPage.yourName}</span>
                    <span className="text-gray-400">Layout</span>
                    <span className="text-white capitalize">{selectedLandingPage.layoutType?.replace(/-/g, " ") || "Classic"}</span>
                    <span className="text-gray-400">URL</span>
                    <span className="text-white text-xs break-all">{window.location.origin}/event/{selectedLandingPage.landingPageSlug}</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-y-2 text-sm">
                    <span className="text-gray-400">Type</span>
                    <span className="text-white">Built-in Landing Page</span>
                    <span className="text-gray-400">Registration</span>
                    <span className="text-white">{formData.requireRegistration ? "Required" : "Not required"}</span>
                    <span className="text-gray-400">Hero Image</span>
                    <span className="text-white">{files.registrationHeroImage ? files.registrationHeroImage.name : existingImageUrls.registrationHeroImage ? "Uploaded" : "None"}</span>
                    <span className="text-gray-400">Replay Registration</span>
                    <span className="text-white">{formData.requireRegistrationForReplay ? "Required" : "Not required"}</span>
                    <span className="text-gray-400">Passcode</span>
                    <span className="text-white">{formData.requirePasscode ? "Enabled" : "Off"}</span>
                    <span className="text-gray-400">Form Fields</span>
                    <span className="text-white">{formData.registrationFields.filter(f => f.enabled).map(f => f.name).join(", ")}</span>
                  </div>
                )}
              </div>

              {/* Email Settings */}
              <div className="border border-white/10 rounded-xl p-4 bg-white/5 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[#832CFF] text-xs font-semibold uppercase tracking-wider">Email Settings</span>
                  <button type="button" onClick={() => goToStep(3)} className="text-[#832CFF] text-xs hover:underline">Edit</button>
                </div>
                <div className="grid grid-cols-2 gap-y-2 text-sm">
                  <span className="text-gray-400">From Name</span>
                  <span className="text-white">{formData.emailFromName || "—"}</span>
                  <span className="text-gray-400">Reply-To</span>
                  <span className="text-white">{formData.emailReplyTo || "—"}</span>
                  <span className="text-gray-400">Confirmation Email</span>
                  <span className={formData.confirmationEmailEnabled ? "text-green-400" : "text-gray-500"}>{formData.confirmationEmailEnabled ? "On" : "Off"}</span>
                  <span className="text-gray-400">24-Hour Reminder</span>
                  <span className={formData.reminderEmail24h ? "text-green-400" : "text-gray-500"}>{formData.reminderEmail24h ? "On" : "Off"}</span>
                  <span className="text-gray-400">1-Hour Reminder</span>
                  <span className={formData.reminderEmail1h ? "text-green-400" : "text-gray-500"}>{formData.reminderEmail1h ? "On" : "Off"}</span>
                  <span className="text-gray-400">Replay Email</span>
                  <span className={formData.replayEmailEnabled ? "text-green-400" : "text-gray-500"}>{formData.replayEmailEnabled ? "On" : "Off"}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Step Navigation Footer ───────────────────────────────── */}
        <div className="flex items-center justify-between pt-4 pb-8">
          <div>
            {currentStep > 1 && (
              <button type="button" onClick={() => goToStep(currentStep - 1)}
                className="flex items-center gap-2 px-5 py-2.5 text-sm text-gray-300 border border-gray-600 rounded-lg hover:bg-white/5 transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
            {currentStep === 1 && (
              <button type="button" onClick={() => navigate("/dashboard/schedule")}
                className="px-5 py-2.5 text-sm text-gray-300 border border-gray-600 rounded-lg hover:bg-white/5 transition-colors">Cancel</button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => handleSubmit(null, "draft")} disabled={isSubmitting}
              className="flex items-center gap-2 px-5 py-2.5 text-sm text-white border border-gray-600 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50">
              <Save className="w-4 h-4" /> Save Draft
            </button>
            {currentStep < 4 && (
              <button type="button" onClick={() => goToStep(currentStep + 1)}
                className="flex items-center gap-2 px-5 py-2.5 text-sm text-white bg-[#832CFF] hover:bg-[#6a1fbf] rounded-lg transition-colors">
                Next <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {(currentStep === 4 || isEditMode) && (
              <button type="button" onClick={(e) => handleSubmit(e, "scheduled")} disabled={isSubmitting}
                className="flex items-center gap-2 px-5 py-2.5 text-sm text-white bg-[#832CFF] hover:bg-[#6a1fbf] rounded-lg transition-colors disabled:opacity-50">
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : isEditMode ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {isEditMode ? "Update Webinar" : "Publish Webinar"}
              </button>
            )}
          </div>
        </div>
      </form>

      {/* ── Share Modal ──────────────────────────────────────────── */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl w-full max-w-md p-6 mx-4 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-white text-lg font-bold flex items-center gap-2">
                <Check className="w-5 h-5 text-green-400" /> Webinar Created!
              </h2>
              <button onClick={() => { setShowShareModal(false); navigate("/dashboard/schedule"); }}
                className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-gray-400 text-sm">Share your event landing page with potential attendees.</p>

            {/* Event Landing Page URL */}
            <div>
              <label className="text-white text-xs font-medium">Event Landing Page URL</label>
              <div className="flex gap-2 mt-1">
                <input type="text" readOnly value={createdWebinarUrl}
                  className="flex-1 px-3 py-2.5 border border-gray-600 rounded-lg bg-gray-800/50 text-white text-sm" />
                <button onClick={handleCopyUrl}
                  className="px-4 py-2.5 bg-[#832CFF] hover:bg-[#6a1fbf] text-white rounded-lg transition-colors flex items-center gap-2 text-sm">
                  <Copy className="w-4 h-4" /> Copy
                </button>
              </div>
            </div>

            {/* Social Share Buttons */}
            <div>
              <label className="text-white text-xs font-medium mb-2 block">Share on</label>
              <SocialShareButtons url={createdWebinarUrl} title={formData.webinarTitle} formData={formData} />
            </div>

            {/* Calendar Download */}
            <CalendarShareDropdown
              label="Choose your calendar"
              title={formData.webinarTitle || "Webinar"}
              description={(() => {
                const raw = formData.webinarDescription
                  ? formData.webinarDescription.replace(/<[^>]*>/g, "").trim()
                  : (formData.description || "").trim();
                return (raw + (createdWebinarUrl ? `\n\n${createdWebinarUrl}` : "")).trim();
              })()}
              startTime={getCalendarStartUtc()}
              durationMinutes={formData.customDuration ? parseInt(formData.customDuration) : parseInt(formData.duration)}
              location={createdWebinarUrl || ""}
              filename={formData.webinarTitle ? formData.webinarTitle.replace(/\s+/g, "-") : "webinar"}
            />

            {/* Done */}
            <button onClick={() => { setShowShareModal(false); navigate("/dashboard/schedule"); }}
              className="w-full px-4 py-2.5 bg-[#832CFF] hover:bg-[#6a1fbf] text-white rounded-lg transition-colors text-sm font-medium">
              Done
            </button>
          </div>
        </div>
      )}
      {/* ── Create Event Landing Page Modal ────────────────────── */}
      <EventLandingPageModal
        isOpen={showCreateLandingPageModal}
        isEdit={false}
        initialData={null}
        onClose={() => setShowCreateLandingPageModal(false)}
        onSubmit={handleCreateLandingPage}
      />
    </div>
  );
};

export default WebinarScheduleForm;
